import React, { useState, useCallback } from 'react';
import {
    View,
    Text,
    StyleSheet,
    TouchableOpacity,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useForm, Controller } from 'react-hook-form';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { CustomInput } from '../components/CustomInput';
import { DatePickerInput } from '../components/DatePickerInput';
import { AppModal, ModalType } from '../components/AppModal';
import { LoadingOverlay } from '../components/LoadingOverlay';
import { theme } from '../constants/theme';
import { ScreenHeader } from '../components/ScreenHeader';
import ApiService from '../services/ApiService';
import { Customer, CustomerType } from '../models/types';
import { generateId, numericValidation, alphabetOnlyWithSpace } from '../utils/helpers';

interface CustomerFormData {
    name: string;
    phone: string;
    address: string;
    notes: string;
    date: string;
}

const AddEditCustomerScreen = ({ navigation, route }: any) => {
    const existingCustomer = route.params?.customer as Customer | undefined;
    const defaultType = route.params?.customerType as CustomerType || 'sale';
    const isEditMode = !!existingCustomer;

    const [loading, setLoading] = useState(false);
    const [customerType, setCustomerType] = useState<CustomerType>(existingCustomer?.customerType || defaultType);
    const [modal, setModal] = useState<{
        visible: boolean;
        type: ModalType;
        title: string;
        message: string;
        isSuccess?: boolean;
    }>({ visible: false, type: 'info', title: '', message: '' });

    const { control, handleSubmit, formState: { errors, isValid } } = useForm<CustomerFormData>({
        defaultValues: {
            name: existingCustomer?.name || '',
            phone: existingCustomer?.phone || '',
            address: existingCustomer?.address || '',
            notes: existingCustomer?.notes || '',
            date: existingCustomer?.createdAt || new Date().toISOString(),
        },
        mode: 'onChange',
        criteriaMode: 'all',
    });

    const handleModalClose = () => {
        const isSuccess = modal.isSuccess;
        setModal(prev => ({ ...prev, visible: false }));
        if (isSuccess) {
            navigation.goBack();
        }
    };

    const onSubmit = useCallback(async (data: CustomerFormData) => {
        setLoading(true);
        try {
            const now = new Date().toISOString();
            const customer: Customer = {
                id: existingCustomer?.id || generateId('CUST'),
                name: data.name.trim(),
                phone: data.phone.trim() || undefined,
                address: data.address.trim() || undefined,
                customerType,
                notes: data.notes.trim() || undefined,
                createdAt: data.date,
                updatedAt: now,
            };

            if (isEditMode) {
                await ApiService.updateCustomer(customer.id, customer);
                setModal({
                    visible: true,
                    type: 'success',
                    title: 'Success',
                    message: 'Customer updated successfully',
                    isSuccess: true,
                });
            } else {
                await ApiService.addCustomer(customer);
                setModal({
                    visible: true,
                    type: 'success',
                    title: 'Success',
                    message: 'Customer added successfully',
                    isSuccess: true,
                });
            }
        } catch (error) {
            console.error('Error saving customer:', error);
            setModal({
                visible: true,
                type: 'error',
                title: 'Error',
                message: 'Failed to save customer. Please try again.',
            });
        } finally {
            setLoading(false);
        }
    }, [existingCustomer, customerType, isEditMode]);

    return (
        <SafeAreaView style={styles.container} edges={['top']}>
            <ScreenHeader
                title={isEditMode ? 'Edit Customer' : 'Add Customer'}
                showBackButton={true}
                navigation={navigation}
            />

            <KeyboardAwareScrollView
                style={styles.scrollView}
                contentContainerStyle={styles.contentContainer}
                showsVerticalScrollIndicator={false}
                bottomOffset={20}
            >
                <View style={styles.form}>
                    {/* Customer Type Selector (only for new customers) */}
                    {!isEditMode && (
                        <View style={styles.typeSelector}>
                            <TouchableOpacity
                                style={[styles.typeButton, customerType === 'sale' && styles.typeButtonActive]}
                                onPress={() => setCustomerType('sale')}
                            >
                                <Icon
                                    name="account"
                                    size={20}
                                    color={customerType === 'sale' ? '#fff' : theme.colors.text}
                                />
                                <Text style={[styles.typeButtonText, customerType === 'sale' && styles.typeButtonTextActive]}>
                                    Buyer
                                </Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                                style={[styles.typeButton, customerType === 'purchase' && styles.typeButtonActive]}
                                onPress={() => setCustomerType('purchase')}
                            >
                                <Icon
                                    name="account-tie"
                                    size={20}
                                    color={customerType === 'purchase' ? '#fff' : theme.colors.text}
                                />
                                <Text style={[styles.typeButtonText, customerType === 'purchase' && styles.typeButtonTextActive]}>
                                    Supplier
                                </Text>
                            </TouchableOpacity>
                        </View>
                    )}

                    <Controller
                        control={control}
                        name="date"
                        rules={{ required: 'Date is required' }}
                        render={({ field: { onChange, value } }) => (
                            <DatePickerInput
                                label="Add Date *"
                                value={value}
                                onChangeDate={onChange}
                                error={errors.date?.message}
                                maximumDate={new Date()}
                            />
                        )}
                    />

                    <Controller
                        control={control}
                        name="name"
                        rules={{ required: 'Name is required' }}
                        render={({ field: { onChange, onBlur, value } }) => (
                            <CustomInput
                                label="Name *"
                                value={value}
                                onChangeText={(text) => onChange(alphabetOnlyWithSpace(text))}
                                onBlur={onBlur}
                                icon="account"
                                error={errors.name?.message}
                                placeholder={customerType === 'sale' ? 'e.g., Raj Dairy' : 'e.g., Local Farmer'}
                            />
                        )}
                    />

                    <Controller
                        control={control}
                        name="phone"
                        rules={{
                            required: 'Phone number is required',
                            minLength: {
                                value: 9,
                                message: 'Phone number must be at least 9 digits',
                            },
                            maxLength: {
                                value: 10,
                                message: 'Phone number must not exceed 10 digits',
                            },
                        }}
                        render={({ field: { onChange, onBlur, value } }) => (
                            <CustomInput
                                label="Phone Number *"
                                value={value}
                                onChangeText={(text) => onChange(numericValidation(text))}
                                onBlur={onBlur}
                                icon="phone"
                                keyboardType="number-pad"
                                placeholder="e.g., 9876543210"
                                error={errors.phone?.message}
                                maxLength={10}
                            />
                        )}
                    />

                    <Controller
                        control={control}
                        name="address"
                        render={({ field: { onChange, onBlur, value } }) => (
                            <CustomInput
                                label="Address"
                                value={value}
                                onChangeText={onChange}
                                onBlur={onBlur}
                                icon="map-marker"
                                placeholder="e.g., Village name, District"
                                multiline
                                numberOfLines={2}
                            />
                        )}
                    />

                    <Controller
                        control={control}
                        name="notes"
                        render={({ field: { onChange, onBlur, value } }) => (
                            <CustomInput
                                label="Notes"
                                value={value}
                                onChangeText={onChange}
                                onBlur={onBlur}
                                icon="note-text"
                                placeholder="Any additional information..."
                                multiline
                                numberOfLines={3}
                            />
                        )}
                    />

                    {/* Action Buttons */}
                    <View style={styles.buttonRow}>
                        <TouchableOpacity
                            style={styles.cancelBtn}
                            onPress={() => navigation.goBack()}
                            activeOpacity={0.7}
                        >
                            <Text style={styles.cancelBtnText}>Cancel</Text>
                        </TouchableOpacity>

                        <TouchableOpacity
                            style={[styles.saveBtn, !isValid && styles.saveBtnDisabled]}
                            onPress={handleSubmit(onSubmit)}
                            activeOpacity={0.7}
                            disabled={loading}
                        >
                            <Text style={styles.saveBtnText}>
                                {loading ? 'Saving...' : isEditMode ? 'Update' : 'Save'}
                            </Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </KeyboardAwareScrollView>

            <AppModal
                visible={modal.visible}
                type={modal.type}
                title={modal.title}
                message={modal.message}
                onClose={handleModalClose}
            />

            <LoadingOverlay visible={loading} message="Saving..." />
        </SafeAreaView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background,
    },
    scrollView: {
        flex: 1,
    },
    contentContainer: {
        flexGrow: 1,
    },
    form: {
        padding: theme.spacing.lg,
    },
    typeSelector: {
        flexDirection: 'row',
        marginBottom: theme.spacing.lg,
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.md,
        padding: theme.spacing.xs,
    },
    typeButton: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: theme.spacing.sm,
        borderRadius: theme.borderRadius.sm,
        gap: 8,
    },
    typeButtonActive: {
        backgroundColor: theme.colors.primary,
    },
    typeButtonText: {
        ...theme.typography.body2,
        color: theme.colors.text,
        fontWeight: '600',
    },
    typeButtonTextActive: {
        color: '#fff',
    },
    buttonRow: {
        flexDirection: 'row',
        gap: theme.spacing.md,
        marginTop: theme.spacing.lg,
    },
    cancelBtn: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
       height: 45,
        borderRadius: theme.borderRadius.lg,
        backgroundColor: theme.colors.surface,
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    cancelBtnText: {
        ...theme.typography.button,
        color: theme.colors.textSecondary,
        fontWeight: '600',
    },
    saveBtn: {
        flex: 2,
        alignItems: 'center',
        justifyContent: 'center',
        height: 45,
        borderRadius: theme.borderRadius.lg,
        backgroundColor: theme.colors.primary,
        ...theme.shadows.sm,
    },
    saveBtnText: {
        ...theme.typography.button,
        color: '#FFFFFF',
        fontWeight: '700',
    },
    saveBtnDisabled: {
        opacity: 0.6,
    },
});

export default AddEditCustomerScreen;
